export type NewsInformationCategory = {
  id: string;
  label: string;
  placeholder?: string;
  helperText?: string;
  keywordLabel?: string;
};

export type NewsInformationCategoryGroup = {
  title: string;
  categories: NewsInformationCategory[];
};

export const NEWS_INFORMATION_CATEGORY_GROUPS: NewsInformationCategoryGroup[] = [
  {
    title: "仕事・経営",
    categories: [
      { id: "ai-generative-ai", label: "AI・生成AI", placeholder: "例：AIエージェント、生成AI規制、業務活用" },
      { id: "tech-gadget", label: "テック・ガジェット", placeholder: "例：iPhone新機能、半導体、生成AI端末" },
      { id: "economy", label: "経済・景気", placeholder: "例：物価、金利、賃上げ" },
      { id: "markets", label: "投資・株式市場", placeholder: "例：日経平均、米国株、為替" },
      { id: "startup-funding", label: "スタートアップ・資金調達", placeholder: "例：SaaS資金調達、IPO、生成AIスタートアップ" },
      { id: "smb-management", label: "中小企業・経営", placeholder: "例：人手不足、価格転嫁、事業承継" },
      { id: "law-administration", label: "法改正・行政", placeholder: "例：電子帳簿保存法、労務改正、補助制度" },
      { id: "subsidies", label: "補助金・助成金", placeholder: "例：IT導入補助金、設備投資、自治体助成" },
      { id: "sales-marketing", label: "営業・マーケティング", placeholder: "例：BtoB営業、SNS集客、価格改定" },
      { id: "hr-labor", label: "人材・労務", placeholder: "例：採用市場、最低賃金、リスキリング" },
      { id: "security", label: "セキュリティ", placeholder: "例：ランサムウェア、個人情報漏えい、フィッシング" },
      {
        id: "industry-trends",
        label: "業界動向",
        placeholder: "例：飲食業界、建設業界、医療業界",
        helperText: "追いたい業界名を入れると、その業界の動きを優先します。",
      },
    ],
  },
  {
    title: "政治・社会・国際",
    categories: [
      { id: "politics", label: "政治", placeholder: "例：政策、選挙、税制" },
      { id: "social-news", label: "社会ニュース", placeholder: "例：防災、教育、交通インフラ" },
      { id: "international", label: "国際ニュース", placeholder: "例：米国、中国、国際情勢" },
    ],
  },
  {
    title: "スポーツ・趣味・生活",
    categories: [
      { id: "sports-general", label: "スポーツ総合", placeholder: "例：代表戦、移籍情報、注目選手" },
      { id: "baseball", label: "野球", placeholder: "例：大谷翔平、プロ野球、MLB移籍" },
      { id: "soccer", label: "サッカー", placeholder: "例：日本代表、Jリーグ、欧州移籍" },
      { id: "golf", label: "ゴルフ", placeholder: "例：国内ツアー、松山英樹、女子ゴルフ" },
      {
        id: "combat-sports",
        label: "大会・注目イベント",
        placeholder: "例：格闘技大会、世界大会、決勝戦",
        keywordLabel: "格闘技・大きな大会",
      },
      { id: "entertainment", label: "エンタメ", placeholder: "例：芸能ニュース、配信作品、話題のイベント" },
      { id: "movies-drama", label: "映画・ドラマ", placeholder: "例：新作映画、国内ドラマ、配信ランキング" },
      { id: "music", label: "音楽", placeholder: "例：ライブ情報、新曲、音楽フェス" },
      { id: "food-restaurants", label: "グルメ・飲食店", placeholder: "例：神田の新店、ラーメン、価格改定" },
      { id: "local-news", label: "地域ニュース", placeholder: "例：神田、千代田区、駅前再開発" },
      { id: "health", label: "健康", placeholder: "例：睡眠、感染症、健康経営" },
      { id: "fitness-running", label: "筋トレ・ランニング", placeholder: "例：マラソン大会、筋トレ習慣、健康アプリ" },
      { id: "outdoor-camping", label: "アウトドア・キャンプ", placeholder: "例：キャンプ場、登山、アウトドア用品" },
      { id: "travel", label: "旅行", placeholder: "例：インバウンド、温泉、航空券" },
      { id: "saving-points", label: "ポイ活・節約", placeholder: "例：ポイント還元、電気代、キャッシュレス" },
      { id: "games-hobbies", label: "ゲーム・趣味", placeholder: "例：新作ゲーム、趣味イベント、ボードゲーム" },
    ],
  },
];

const NEWS_INFORMATION_CATEGORIES = NEWS_INFORMATION_CATEGORY_GROUPS.flatMap((group) => group.categories);

export function findNewsInformationCategory(categoryId: string): NewsInformationCategory | undefined {
  return NEWS_INFORMATION_CATEGORIES.find((category) => category.id === categoryId);
}

export function normalizeNewsInformationKeyword(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

export function buildNewsInformationKeyword(categoryId: string, priorityTopic: string): string {
  const category = findNewsInformationCategory(categoryId);
  return [category?.keywordLabel ?? category?.label ?? "", normalizeNewsInformationKeyword(priorityTopic)]
    .filter(Boolean)
    .join(" ");
}

export function buildNewsInformationTitle(categoryId: string): string {
  const category = findNewsInformationCategory(categoryId);
  return category ? `${category.label}ニュース` : "";
}
